import { CheckIcon, XIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import type { ChatUiApprovalRequest } from '@voll-intelligence/genui-contract';
import { CardFooter } from '../../../shared/ui/card.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../shared/ui/flight_ui.css';
import '../../shared/approval.css';

/**
 * Card de aprovação de voucher (`approval_request`) — o guardrail determinístico
 * da decisão: os dois botões (Aprovar / Reprovar) ecoam o MESMO `challengeId`
 * ESTRUTURADO (nunca texto interpretado pelo LLM) MAIS a `decision` escolhida e
 * a `justification` opcional digitada pelo aprovador. O backend verifica o
 * challenge, re-autoriza o aprovador e fence exactly-once server-side antes de
 * publicar. Fatos do voucher são display-only (vêm do integrator, nunca do LLM).
 * Componente fechado: resumo + campo + CTAs, congela com `readOnly`.
 */
export function ApprovalRequestCard({
  request,
  note,
  readOnly = false,
  onDecide,
}: {
  readonly request: ChatUiApprovalRequest;
  readonly note?: string;
  readonly readOnly?: boolean;
  readonly onDecide?: (decision: 'approve' | 'deny', justification?: string) => void;
}) {
  const { t } = useGenUiI18n();
  // Trava local anti duplo-clique (handoff: rótulo explícito, nada de estado mudo).
  // O challenge é single-use no servidor — a trava real é server-side.
  const [sent, setSent] = useState(false);
  const [justification, setJustification] = useState('');
  const showJustify = request.justificationAllowed && !readOnly;
  const submit = (decision: 'approve' | 'deny') => {
    setSent(true);
    const trimmed = justification.trim();
    onDecide?.(decision, decision === 'deny' && trimmed.length > 0 ? trimmed : undefined);
  };
  return (
    <section className="fui" aria-label={t('approval.requestAria')}>
      <div className="fui-card">
        <div className="fui-body">
          <div className="fui-cf-summary">
            <span className="fui-k">{t('approval.voucher')}</span>
            <span className="fui-v is-mono">{request.voucherCode}</span>
            <span className="fui-k">{t('approval.modal')}</span>
            <span className="fui-v">{request.modal}</span>
            <span className="fui-k">{t('approval.traveler')}</span>
            <span className="fui-v">{request.traveler.name}</span>
            {request.approver && (
              <>
                <span className="fui-k">{t('approval.approver')}</span>
                <span className="fui-v">{request.approver.name}</span>
              </>
            )}
          </div>
          {showJustify && (
            <label className="appr-justify">
              <span className="appr-justify-label">{t('approval.justificationOptional')}</span>
              <textarea
                value={justification}
                onChange={(e) => {
                  setJustification(e.target.value);
                }}
                disabled={sent}
                maxLength={500}
                placeholder={t('approval.reasonDecision')}
              />
            </label>
          )}
        </div>
        <CardFooter note={note}>
          {!readOnly && (
            <div className="fui-actions">
              <button
                type="button"
                className="fui-btn fui-btn-primary fui-btn-lg"
                disabled={sent || !onDecide}
                onClick={() => {
                  submit('approve');
                }}
              >
                <CheckIcon size={14} aria-hidden />
                {sent ? t('approval.sent') : t('approval.approve')}
              </button>
              <button
                type="button"
                className="fui-btn fui-btn-deny fui-btn-lg"
                disabled={sent || !onDecide}
                onClick={() => {
                  submit('deny');
                }}
              >
                <XIcon size={14} aria-hidden />
                {t('approval.deny')}
              </button>
            </div>
          )}
        </CardFooter>
      </div>
    </section>
  );
}

/* ── Ícones (SVG inline, traço phosphor-like; sem dependência) ───────── */
